import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { LOAD_MORE_PRODUCTS_ACTION } from '~/routes/actions.load-more-products';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import { SIMILAR_PRODUCTS_PAGE_SIZE } from '@modules/product/util/similar-products-config';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';

interface LoadMoreResponse {
    products: TileViewModel<ProductTileViewModel>[];
    hasMore: boolean;
    totalCount: number;
}

export interface SimilarProducts {
    /** Trimmed to complete grid rows while more pages remain. */
    tiles: DetailTile[];
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
    /** True when the SSR'd first page has products (gates the section / row). */
    hasItems: boolean;
}

/**
 * Shared data for the "Similar Products" grid. The first page is SSR'd by the
 * product loader (`similarProducts`); this hook holds the later pages appended
 * via "Load More" (the `/actions/load-more-products` directory search) and
 * merges them. Used by both the desktop accordion row and the mobile standalone
 * section, so only one instance mounts per breakpoint.
 */
export const useSimilarProducts = (): SimilarProducts => {
    const { product, primaryCategory, similarProducts } =
        useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const pageSize = mobile
        ? SIMILAR_PRODUCTS_PAGE_SIZE.mobile
        : SIMILAR_PRODUCTS_PAGE_SIZE.desktop;
    const columns = mobile ? 2 : 4;

    // Page 0 from the loader (SSR); Remix serialises it to JsonifyObject.
    const initialProducts = useMemo(
        () =>
            (similarProducts?.products ??
                []) as unknown as TileViewModel<ProductTileViewModel>[],
        [similarProducts],
    );

    const fetcher = useFetcher<LoadMoreResponse>();
    // Only the "Load More" pages live in state; page 0 comes from the loader.
    const [appended, setAppended] = useState<
        TileViewModel<ProductTileViewModel>[]
    >([]);
    const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);
    const nextOffsetRef = useRef<number>(pageSize);
    const requestedOffsetRef = useRef(-1);
    const processedOffsetRef = useRef(-1);

    const categoryLink = primaryCategory?.Link?.replace(/\/+$/, '') ?? '';

    // Reset the appended pages when navigating to a different product/category
    // (the loader supplies a fresh page 0).
    const categoryKeyRef = useRef(categoryLink);
    useEffect(() => {
        if (categoryKeyRef.current === categoryLink) return;
        categoryKeyRef.current = categoryLink;
        setAppended([]);
        setServerHasMore(null);
        nextOffsetRef.current = pageSize;
        requestedOffsetRef.current = -1;
        processedOffsetRef.current = -1;
    }, [categoryLink, pageSize]);

    const fetchPage = useCallback(
        (offset: number) => {
            if (!categoryLink || requestedOffsetRef.current === offset) return;
            requestedOffsetRef.current = offset;
            const params = new URLSearchParams({
                categoryLink,
                offset: String(offset),
                limit: String(pageSize),
            });
            fetcher.load(`${LOAD_MORE_PRODUCTS_ACTION}?${params.toString()}`);
        },
        [categoryLink, fetcher, pageSize],
    );

    // Append each "Load More" response once, excluding the current product and
    // anything already loaded (loader page 0 + earlier appended pages).
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (processedOffsetRef.current === requestedOffsetRef.current) return;
        processedOffsetRef.current = requestedOffsetRef.current;

        const incoming = (fetcher.data.products ??
            []) as unknown as TileViewModel<ProductTileViewModel>[];
        setAppended((prev) => {
            const seen = new Set(
                [...initialProducts, ...prev].map((tile) => tile.model.id),
            );
            const fresh = incoming.filter(
                (tile) =>
                    tile.model.id !== product.ID && !seen.has(tile.model.id),
            );
            return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerHasMore(Boolean(fetcher.data.hasMore));
        nextOffsetRef.current = requestedOffsetRef.current + pageSize;
    }, [fetcher.state, fetcher.data, product.ID, initialProducts, pageSize]);

    // Stable across renders (only fetchPage's deps move it) so consumers that
    // memoise on it — e.g. the Magic Island load-more mode — don't thrash.
    const loadMore = useCallback(
        () => fetchPage(nextOffsetRef.current),
        [fetchPage],
    );

    const items = useMemo(
        () => [...initialProducts, ...appended],
        [initialProducts, appended],
    );
    const adminData = useItemAdminData(items.map((tile) => tile.model.id));

    const allTiles: DetailTile[] = items.map(({ model }) => ({
        id: model.id,
        link: model.link,
        title: model.title,
        images: model.images,
        subtitle: model.professional?.name,
        professional: {
            id: model.professional?.id,
            name: model.professional?.name,
        },
        pinType: 'ProductPin',
        adminData: adminData[model.id],
        professionalLink: model.professional?.link,
        // The item's own site, which arms the hover panel's Visit site.
        website: model.website,
    }));

    const hasMore = serverHasMore ?? Boolean(similarProducts?.hasMore);

    // While more pages remain, show only complete rows (4 cols desktop / 2
    // mobile) so the current-product exclusion or a short page never leaves a
    // ragged row; the trimmed tiles fill in on the next "Load More".
    const fullRowCount = Math.floor(allTiles.length / columns) * columns;
    const tiles =
        hasMore && fullRowCount >= columns
            ? allTiles.slice(0, fullRowCount)
            : allTiles;

    return {
        tiles,
        hasMore,
        isLoading: fetcher.state !== 'idle',
        loadMore,
        hasItems: initialProducts.length > 0,
    };
};
